// Pure web-DM thread helpers. Import-free so tests/dm-thread.test.mjs can load
// this file in Node (same contract as src/utils/program.js).
//
// SocialAPI.sendDM currently returns the log_event JSON on HTTP 2xx and null
// on failure. The API contract for a confirmed send is {ok, id, ts}; the
// existing {success, event:{id, created_at}} shape is also accepted.

export const THREAD_CAP = 200;
export const ECHO_WINDOW_MS = 120000;
// Server created_at is at-or-after the optimistic ts. Allow a tiny negative skew
// for clock differences, but never match an older identical history row.
export const ECHO_SKEW_MS = 500;

export function tsMs(ts) {
  const n = Date.parse(ts);
  return Number.isFinite(n) ? n : NaN;
}

export function isReadBy(ts, friendLastRead) {
  if (!ts || !friendLastRead) return false;
  const a = tsMs(ts), b = tsMs(friendLastRead);
  if (Number.isFinite(a) && Number.isFinite(b)) return a <= b;
  return String(ts) <= String(friendLastRead);
}

export function isUnconfirmed(m) {
  if (!m) return false;
  return m.status === "pending" || m.status === "failed" || m.pending === true || m.failed === true;
}

export function isLocalish(m) {
  if (!m) return false;
  if (m.local === true) return true;
  if (isUnconfirmed(m)) return true;
  if (typeof m.id === "string" && m.id.startsWith("poll-")) return true;
  return false;
}

/** Parse sendDM / sendDMImage return values into a uniform {ok,id,ts}. */
export function parseSendResult(result) {
  if (result == null || result === false) return { ok: false, id: null, ts: null, error: null };
  if (typeof result !== "object") return { ok: false, id: null, ts: null, error: null };
  if (result.ok === false || result.success === false) {
    return { ok: false, id: null, ts: null, error: result.error || null };
  }
  const hasOk = result.ok === true || result.success === true;
  if (result.error && !hasOk) {
    return { ok: false, id: null, ts: null, error: result.error };
  }
  const event = result.event && typeof result.event === "object" ? result.event : null;
  const message = result.message && typeof result.message === "object" ? result.message : null;
  const id = result.id ?? event?.id ?? message?.id ?? null;
  const ts = result.ts ?? event?.ts ?? event?.created_at ?? message?.ts ?? message?.created_at ?? result.created_at ?? null;
  if (hasOk) return { ok: true, id: id != null ? String(id) : null, ts: ts || null };
  if (event && event.id) return { ok: true, id: String(event.id), ts: ts || null };
  if (id) return { ok: true, id: String(id), ts: ts || null };
  return { ok: false, id: null, ts: null, error: result.error || null };
}

export function normalizeServerMessage(m, { email, friendLastRead } = {}) {
  if (!m || typeof m !== "object") return null;
  const imageData = m.imageData || m.data?.imageData || null;
  const gifUrl = m.gifUrl || m.data?.gifUrl || null;
  const snap = m.snap ?? m.data?.snap ?? null;
  const from = m.from || m.user_email || "";
  const isOwn = !!(m.isOwn || (email && from === email));
  const rawText = m.text || m.data?.text || "";
  let text = rawText;
  if (snap != null && (!text || text === "📷 Photo")) text = "📸 Gym Snap";
  else if (gifUrl && (!text || text === "📷 Photo")) text = "GIF";
  else if (imageData && !text) text = "📷 Photo";
  const ts = m.ts || m.created_at || "";
  const id = m.id != null ? String(m.id) : `${from}-${ts}`;
  const unconfirmed = isUnconfirmed(m);
  return {
    id,
    from,
    name: m.name || "",
    text,
    ts,
    isOwn,
    imageData,
    gifUrl,
    snap,
    read: !unconfirmed && isOwn && isReadBy(ts, friendLastRead),
    status: m.status === "pending" || m.status === "failed" ? m.status : "sent",
    local: m.local === true,
    pending: m.status === "pending" || m.pending === true,
    failed: m.status === "failed" || m.failed === true,
    localId: m.localId || null,
    baselineServerIDs: Array.isArray(m.baselineServerIDs) ? m.baselineServerIDs.map(String) : null,
  };
}

export function buildPendingMessage({ id, email, name, text, imageData, gifUrl, snap, ts, baselineServerIDs = [] }) {
  const img = imageData || null;
  return {
    id,
    from: email,
    name: name || "",
    text: img && !text ? "📷 Photo" : (text || ""),
    imageData: img,
    gifUrl: gifUrl || null,
    snap: snap ?? null,
    ts,
    isOwn: true,
    read: false,
    status: "pending",
    local: true,
    pending: true,
    failed: false,
    localId: id,
    baselineServerIDs: [...baselineServerIDs].map(String),
  };
}

function payloadCompatible(server, local) {
  return (server.text || "") === (local.text || "")
    && (server.imageData || null) === (local.imageData || null)
    && (server.gifUrl || null) === (local.gifUrl || null)
    && (server.snap ?? null) === (local.snap ?? null);
}

function findEchoIndex(local, unmatched) {
  let best = -1, bestDt = Infinity;
  const localMs = tsMs(local.ts);
  if (!Number.isFinite(localMs)) return -1;
  for (let i = 0; i < unmatched.length; i++) {
    const sv = unmatched[i];
    if (!sv.isOwn) continue;
    if ((local.baselineServerIDs || []).map(String).includes(String(sv.id))) continue;
    if (!payloadCompatible(sv, local)) continue;
    const svMs = tsMs(sv.ts);
    if (!Number.isFinite(svMs)) continue;
    if (svMs < localMs - ECHO_SKEW_MS) continue;
    const dt = svMs - localMs;
    if (dt > ECHO_WINDOW_MS) continue;
    const closeness = Math.abs(dt);
    if (closeness < bestDt) {
      best = i;
      bestDt = closeness;
    }
  }
  return best;
}

function preferServer(server, local) {
  return {
    ...local,
    ...server,
    id: server.id,
    ts: server.ts || local.ts,
    imageData: server.imageData || local.imageData || null,
    gifUrl: server.gifUrl || local.gifUrl || null,
    snap: server.snap ?? local.snap ?? null,
    text: server.text || local.text,
    name: server.name || local.name,
    from: server.from || local.from,
    isOwn: !!(server.isOwn || local.isOwn),
    status: "sent",
    local: false,
    pending: false,
    failed: false,
    localId: local.localId || (local.local ? local.id : null) || local.localId,
    read: false,
  };
}

export function applyReceipts(messages, friendLastRead, email) {
  return (messages || []).map((m) => {
    const isOwn = !!(m.isOwn || (email && m.from === email));
    if (!isOwn || isUnconfirmed(m)) {
      return m.read ? { ...m, read: false } : m;
    }
    const read = isReadBy(m.ts, friendLastRead);
    return m.read === read ? m : { ...m, read };
  });
}

export function applySendAck(messages, localId, result) {
  const parsed = parseSendResult(result);
  const lid = String(localId);
  const updated = (messages || []).map((m) => {
    if (String(m.id) !== lid && String(m.localId || "") !== lid) return m;
    const already = m.status === "sent" && m.local === false;
    if (!parsed.ok) {
      if (already) return m;
      return { ...m, status: "failed", pending: false, failed: true };
    }
    return {
      ...m,
      id: parsed.id ? String(parsed.id) : m.id,
      ts: parsed.ts || m.ts,
      status: "sent",
      local: parsed.id ? false : m.local !== false,
      pending: false,
      failed: false,
      localId: m.localId || lid,
    };
  });
  // A poll may install the explicit server echo before the POST resolves. When
  // the acknowledgement then assigns that same id, retain one server row.
  const byId = new Map();
  for (const message of updated) {
    const id = String(message.id);
    const existing = byId.get(id);
    byId.set(id, existing ? preferServer(message, existing) : message);
  }
  return sortMsgs([...byId.values()]);
}

export function recoverInterruptedMessages(messages, activeIds = []) {
  const active = new Set([...activeIds].map(String));
  return (messages || []).map((message) => {
    const id = String(message.localId || message.id);
    if (message.status !== "pending" || active.has(id)) return message;
    return { ...message, status: "failed", pending: false, failed: true };
  });
}

function sortMsgs(list) {
  return list.slice().sort((a, b) => {
    const da = tsMs(a.ts), db = tsMs(b.ts);
    if (Number.isFinite(da) && Number.isFinite(db) && da !== db) return da - db;
    if ((a.ts || "") !== (b.ts || "")) return String(a.ts || "").localeCompare(String(b.ts || ""));
    return String(a.id).localeCompare(String(b.id));
  });
}

function capThread(list, cap) {
  if (!cap || list.length <= cap) return list;
  const keep = new Set();
  list.forEach((m, i) => { if (isUnconfirmed(m)) keep.add(i); });
  const start = list.length - cap;
  const out = [];
  list.forEach((m, i) => { if (i >= start || keep.has(i)) out.push(m); });
  return out;
}

function tsCmp(a, b) {
  const da = tsMs(a), db = tsMs(b);
  if (Number.isFinite(da) && Number.isFinite(db)) return da - db;
  return String(a || "").localeCompare(String(b || ""));
}

/**
 * Merge a server page into the local thread.
 * mode "latest": keep pending/failed, keep history older than the page, 1:1 echo-match.
 * mode "older": union by id (loadOlder overlaps must not duplicate).
 */
export function mergeThread(prev, incomingRaw, opts = {}) {
  const { email, friendLastRead, cap = THREAD_CAP, mode = "latest" } = opts;
  const incoming = (incomingRaw || [])
    .map((m) => normalizeServerMessage(m, { email, friendLastRead }))
    .filter(Boolean);
  const prevList = Array.isArray(prev) ? prev : [];

  if (mode === "latest" && incoming.length === 0) {
    // Empty pages are not a deletion signal — a stale/racy [] must not wipe
    // a just-acked send or cached history. Pending/failed stay as-is.
    return applyReceipts(sortMsgs(prevList), friendLastRead, email);
  }

  const incomingById = new Map();
  for (const m of incoming) incomingById.set(String(m.id), m);

  const unmatchedOwn = incoming.filter((m) => m.isOwn || (email && m.from === email));
  const used = new Set();
  const result = [];

  const takeIncoming = (sv, local) => {
    incomingById.delete(String(sv.id));
    used.add(sv.id);
    result.push(preferServer(sv, local || sv));
  };

  for (const local of prevList) {
    const lid = String(local.id);
    if (incomingById.has(lid)) {
      takeIncoming(incomingById.get(lid), local);
      continue;
    }
    if (local.serverId && incomingById.has(String(local.serverId))) {
      takeIncoming(incomingById.get(String(local.serverId)), local);
      continue;
    }
    if (lid.startsWith("poll-")) {
      const hit = [...incomingById.values()].find((sv) => sv.from === local.from && sv.ts === local.ts);
      if (hit) { takeIncoming(hit, local); continue; }
    }
    // Pending and failed sends require an explicit POST acknowledgement. A
    // successful response without an id remains a local accepted row and may
    // reconcile conservatively against a new server id outside its baseline.
    if (local.status === "sent" && local.local === true && Array.isArray(local.baselineServerIDs)) {
      const pool = unmatchedOwn.filter((sv) => incomingById.has(String(sv.id)) && !used.has(sv.id));
      const idx = findEchoIndex(local, pool);
      if (idx >= 0) { takeIncoming(pool[idx], local); continue; }
      result.push(local);
      continue;
    }
    if (isLocalish(local)) {
      result.push(local);
      continue;
    }
    if (mode === "older") {
      result.push(local);
      continue;
    }
    // A latest page is not a deletion manifest. Preserve confirmed cached and
    // explicitly-loaded history whose ids are absent from this response.
    result.push(local);
  }

  for (const m of incomingById.values()) result.push(m);

  const byId = new Map();
  for (const m of sortMsgs(result)) {
    const id = String(m.id);
    byId.set(id, byId.has(id) ? preferServer(m, byId.get(id)) : m);
  }
  const deduped = sortMsgs([...byId.values()]);
  const capped = mode === "older" ? deduped : capThread(deduped, cap);
  return applyReceipts(capped, friendLastRead, email);
}
